/**
 * Terms of Service Page
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const TermsOfService = () => {
  const lastUpdated = 'December 5, 2025';

  return (
    <div className="legal-container">
      <div className="legal-card">
        <Link to="/login" className="legal-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Login
        </Link>

        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: {lastUpdated}</p>

        <div className="legal-content">
          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using the FusionICT Billing Platform ("Service"), you agree to be bound by these
              Terms of Service ("Terms"). If you disagree with any part of the Terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              FusionICT Billing is a cloud-based invoicing and billing management platform that enables businesses to:
            </p>
            <ul>
              <li>Create and send professional invoices</li>
              <li>Track billable time and expenses</li>
              <li>Manage client relationships</li>
              <li>Process payments through integrated payment gateways</li>
              <li>Monitor Google Cloud Platform billing and costs</li>
              <li>Generate financial reports and analytics</li>
            </ul>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To use certain features of the Service, you must create an account. You agree to provide accurate,
              current, and complete information during registration and to keep your account information updated.
            </p>

            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all
              activities that occur under your account. You must notify us immediately of any unauthorized access
              or use of your account.
            </p>

            <h3>3.3 Account Restrictions</h3>
            <p>
              Access to the Service is restricted to authorized users with @fusionict.com email addresses or
              approved partner accounts. Unauthorized access attempts are prohibited.
            </p>
          </section>

          <section>
            <h2>4. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Transmit malware, viruses, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Engage in fraudulent activities or misrepresentation</li>
              <li>Collect or harvest user data without consent</li>
              <li>Use automated systems to access the Service without permission</li>
            </ul>
          </section>

          <section>
            <h2>5. Payment Terms</h2>
            <h3>5.1 Fees and Billing</h3>
            <p>
              Certain features of the Service may require payment of fees. All fees are quoted in the currency
              specified and are non-refundable unless otherwise stated. We reserve the right to modify pricing
              with reasonable notice.
            </p>

            <h3>5.2 Payment Processing</h3>
            <p>
              Payments are processed through third-party payment processors (Stripe, PayMongo). By using our
              payment features, you agree to their respective terms of service.
            </p>

            <h3>5.3 Invoicing</h3>
            <p>
              You are responsible for ensuring the accuracy of invoices you create and send through the Service.
              FusionICT is not responsible for errors in user-generated invoices.
            </p>
          </section>

          <section>
            <h2>6. Intellectual Property</h2>
            <h3>6.1 Our Rights</h3>
            <p>
              The Service, including its original content, features, and functionality, is owned by FusionICT
              and is protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h3>6.2 Your Content</h3>
            <p>
              You retain ownership of content you create or upload to the Service. By using the Service, you
              grant us a limited license to store, process, and display your content as necessary to provide
              the Service.
            </p>
          </section>

          <section>
            <h2>7. Data and Privacy</h2>
            <p>
              Your use of the Service is also governed by our <Link to="/privacy">Privacy Policy</Link>, which
              describes how we collect, use, and protect your information. By using the Service, you consent to
              the collection and use of information as described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party services including Google Cloud Platform, Firebase, Stripe,
              and PayMongo. Your use of these integrations is subject to their respective terms of service.
              FusionICT is not responsible for third-party service availability or performance.
            </p>
          </section>

          <section>
            <h2>9. Service Availability</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee uninterrupted access.
              The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond
              our control. We will provide reasonable notice of scheduled maintenance when possible.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, FusionICT shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or corruption of files</li>
              <li>Service interruptions or delays</li>
              <li>Errors or inaccuracies in invoices or calculations</li>
            </ul>
            <p>
              Our total liability for any claim arising from the Service shall not exceed the amount you paid
              to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2>11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
              IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE
              OR UNINTERRUPTED.
            </p>
          </section>

          <section>
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless FusionICT and its officers, directors, employees, and
              agents from any claims, damages, losses, or expenses (including legal fees) arising from your
              use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may terminate or suspend your access to the Service immediately, without prior notice, for
              any reason, including breach of these Terms. Upon termination, your right to use the Service
              will cease immediately. You may request export of your data within 30 days of termination.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the Republic of
              the Philippines, without regard to its conflict of law provisions. Any disputes arising from
              these Terms shall be resolved in the courts of Manila, Philippines.
            </p>
          </section>

          <section>
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of material
              changes by posting the updated Terms on the Service and updating the "Last updated" date.
              Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2>16. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall
              be limited or eliminated to the minimum extent necessary, and the remaining provisions shall
              remain in full force and effect.
            </p>
          </section>

          <section>
            <h2>17. Contact Information</h2>
            <p>For questions about these Terms of Service, please contact us at:</p>
            <div className="contact-info">
              <p><strong>FusionICT</strong></p>
              <p>Email: legal@fusionict.com</p>
              <p>Address: Manila, Philippines</p>
            </div>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="separator">•</span>
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
